import equal from "fast-deep-equal";
import dayjs from "@gtmhub/dayjs";
import { MetricDeadLineEnum, metricFormStrings } from "@gtmhub/okrs/metrics/utils";
import { Links } from "@webapp/links/models/links.models";
import { FormFieldsValues } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.component";
import { MetricFormParams, MetricManualTypeEnum } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.models";
import { Deadline, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { filterWorkflowSelectionProperties, parseDecimalInput } from "@webapp/okrs/utils/metric.utils";

export const DEFAULT_DATE = "0001-01-01T00:00:00Z";
const AUTOMATION_FIELDS_ON_SAVE = ["insightName", "fieldName", "settings"];

export const metricProperties = [
  "name",
  "insightName",
  "fieldName",
  "targetOperator",
  "target",
  "critical",
  "initialValue",
  "actual",
  "manualType",
  "softDueDate",
  "dueDate",
  "description",
  "ownerId",
  "ownerIds",
  "customFields",
  "format",
  "cascadeType",
  "settings",
];

export function getDatesRespectingMetric(metric: Metric, stateParams: MetricFormParams, defaultDates: Deadline): Deadline {
  if (!stateParams.metricId) {
    return defaultDates;
  }

  const dueDateIsDefaultOrNull = !metric.dueDate || metric.dueDate === DEFAULT_DATE;
  const softDueDateIsDefaultOrNull = !metric.softDueDate || metric.softDueDate === DEFAULT_DATE;

  if (softDueDateIsDefaultOrNull && dueDateIsDefaultOrNull) {
    return defaultDates;
  }

  if (softDueDateIsDefaultOrNull && !dueDateIsDefaultOrNull) {
    return {
      useDeadline: MetricDeadLineEnum.HARD_DEADLINE,
      dueDate: metric.dueDate,
      softDueDate: dayjs(metric.dueDate).subtract(1, metricFormStrings.day).toISOString(),
    };
  }

  if (!softDueDateIsDefaultOrNull && !dueDateIsDefaultOrNull) {
    return {
      useDeadline: MetricDeadLineEnum.HARD_AND_SOFT_DEADLINE,
      dueDate: metric.dueDate,
      softDueDate: metric.softDueDate,
    };
  }
}

export function generateDefaultMetricValues(goalOwnerIds: string[], goalId: string): Metric {
  return {
    ownerIds: goalOwnerIds,
    goalId: goalId,
    manualType: MetricManualTypeEnum.DOUBLE,
    targetOperator: "at_least",
    initialValue: null,
    target: null,
    // setting critical to null when creating a new metric
    critical: null,
    name: "",
    dueDate: null,
    softDueDate: null,
    links: <Links<"metric">>{},
    customFields: {},
    format: {
      prefix: "",
      suffix: "",
      fractionSize: 0,
      isCustomUnit: false,
    },
    insightName: "",
  } as Metric;
}

export interface GenerateMetricFromFormOptions {
  workflowEditableKrFields?: string[];
  goalOwnerIds?: string[];
  actual: number;
}

export function createNewMetricFromForm(metric: Metric, formValues: FormFieldsValues, options: GenerateMetricFromFormOptions): Metric {
  const newMetric = angular.copy(metric);

  let properties = angular.copy(metricProperties);
  if (options.workflowEditableKrFields) {
    properties = filterWorkflowSelectionProperties(options.workflowEditableKrFields);
  }

  let normalizedFormValues = normalizeFormValues(formValues, properties);
  if (formValues.type.manualType === "boolean") {
    normalizedFormValues = setNumericValuesForBoolean(normalizedFormValues);
  }

  // If the metric and form properties are not equal, set the metric property to the form property
  properties.forEach((property) => {
    if (!equal(newMetric[property], normalizedFormValues[property])) {
      newMetric[property] = normalizedFormValues[property];
    }
  });

  // If the metric has no ownerIds, set the ownerIds to the goal ownerIds
  if ((!newMetric.ownerIds || newMetric.ownerIds.length === 0) && options.goalOwnerIds) {
    newMetric.ownerIds = options.goalOwnerIds;
  }

  // If actual is null or undefined, set it to the initialValue
  // else they can differ when KR is automated
  if (options.actual === null || options.actual === undefined) {
    newMetric.actual = newMetric.initialValue;
  } else {
    newMetric.actual = options.actual;
  }

  return newMetric;
}

export function createOnlyUpdatedFieldsMetric(metric: Metric, formValues: FormFieldsValues, options: GenerateMetricFromFormOptions): Partial<Metric> {
  const partialMetric: Partial<Metric> = {
    id: metric.id,
  };

  let properties = angular.copy(metricProperties);
  if (options.workflowEditableKrFields) {
    properties = filterWorkflowSelectionProperties(options.workflowEditableKrFields);
  }

  let normalizedFormValues = normalizeFormValues(formValues, properties);
  // if new manualType is boolean, set numeric values
  if (metric.manualType !== formValues.type.manualType && formValues.type.manualType === "boolean") {
    normalizedFormValues = setNumericValuesForBoolean(normalizedFormValues);
  }

  // If the form has no ownerIds, set the ownerIds to the goal ownerIds
  if ((!normalizedFormValues.ownerIds || normalizedFormValues.ownerIds.length === 0) && options.goalOwnerIds) {
    normalizedFormValues = { ...normalizedFormValues, ownerIds: options.goalOwnerIds };
  }

  // Should be set actual to initialValue if it's boolean type metric
  // or to the current actual value if it's not
  if (normalizedFormValues.manualType === "boolean") {
    normalizedFormValues = { ...normalizedFormValues, actual: normalizedFormValues.initialValue };
  } else if (metric.actual !== options.actual) {
    normalizedFormValues = { ...normalizedFormValues, actual: options.actual };
  }

  // Compare old values with new values from the form
  for (const key of Object.keys(normalizedFormValues)) {
    if (!equal(metric[key], normalizedFormValues[key])) {
      partialMetric[key] = normalizedFormValues[key];
    }
  }

  return partialMetric;
}

// Flattens nested form values into a partial metric object
export function normalizeFormValues(formValues: FormFieldsValues, properties: string[] = metricProperties): Partial<Metric> {
  const partialMetric: Partial<Metric> = {};

  for (const property of properties) {
    if (property in formValues) {
      partialMetric[property] = formValues[property];
    }
  }

  // type field combines multiple metric properties into a single object
  const flatType = formValues.type;
  for (const property of properties) {
    if (property in flatType) {
      partialMetric[property] = flatType[property];
    }
  }

  // For automated KRs on save update: "insightName", "fieldName", "settings"
  // other values are updated on change in the form itself
  partialMetric.insightName = formValues.automation.insightName;
  if (formValues.automation && formValues.automation.insightName) {
    for (const property of AUTOMATION_FIELDS_ON_SAVE) {
      if (property in formValues.automation) {
        partialMetric[property] = formValues.automation[property];
      }
    }
  }

  // deadline field combines multiple metric properties into a single object
  const { dueDate, softDueDate } = getMetricDueDates(formValues);
  partialMetric.dueDate = dueDate;
  partialMetric.softDueDate = softDueDate;

  parseDecimalInput(partialMetric, ["actual", "initialValue", "target"]);

  return partialMetric;
}

// Returns new partial metric object with boolean values set to 0, 1, "at_least", "" and null
function setNumericValuesForBoolean(partialMetric: Partial<Metric>): Partial<Metric> {
  const result = angular.copy(partialMetric);

  // setting new boolean initial to 0, target to 1, targetOperator to "at_least", prefix and suffix to ""
  if (result.manualType === "boolean") {
    result.initialValue = 0;
    result.target = 1;

    if (result.format) {
      result.format.prefix = "";
      result.format.suffix = "";
    }

    // boolean metrics targetOperator is always "at_least"
    result.targetOperator = "at_least";
  }

  return result;
}

function getMetricDueDates(form: FormFieldsValues): { dueDate: string; softDueDate: string } {
  const useDeadline = form.deadline.useDeadline;
  const dueDate = form.deadline.dueDate;
  const softDueDate = form.deadline.softDueDate;

  // if no deadlines are set OR/AND metric is anti-target KR --> use Session's end
  if (
    useDeadline === MetricDeadLineEnum.NONE ||
    ((form.type.manualType === MetricManualTypeEnum.DOUBLE || form.type.manualType === MetricManualTypeEnum.EMPTY) &&
      (form.type.targetOperator === "should_stay_above" || form.type.targetOperator === "should_stay_below"))
  ) {
    return {
      dueDate: DEFAULT_DATE,
      softDueDate: DEFAULT_DATE,
    };
  }

  if (useDeadline === MetricDeadLineEnum.HARD_DEADLINE) {
    return {
      dueDate: dayjs(dueDate).endOf("day").toISOString(),
      softDueDate: DEFAULT_DATE,
    };
  }

  if (useDeadline === MetricDeadLineEnum.HARD_AND_SOFT_DEADLINE) {
    return {
      dueDate: dayjs(dueDate).endOf("day").toISOString(),
      softDueDate: dayjs(softDueDate).endOf("day").toISOString(),
    };
  }
}
